// FileUpload with upload preset path as prop, fetches a presigned URL and uploads the file

import { faFileArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";

import { getUploadUrl } from "../services/file-upload";
import { Button } from "./button";
import { Select } from "./select";

export type FileUploadFolderOption = {
  value: string;
  label: string;
};

export function FileUpload({
  path,
  onUpload,
  folderOptions,
  folderLabel,
  disabled = false,
}: {
  path: string;
  onUpload?: (file: File) => void;
  folderOptions?: FileUploadFolderOption[];
  folderLabel?: string;
  disabled?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState("");

  const uploadPath = selectedFolder !== "" ? `${path}/${selectedFolder}` : path;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || disabled) return;

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const uploadUrl = await getUploadUrl({
        filename: file.name,
        path: uploadPath,
      });

      const uploadResp = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResp.ok) {
        throw new Error("Failed to upload file");
      }

      setSuccess(true);
      onUpload?.(file);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error uploading file");
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-2 mt-2">
      {folderOptions && folderOptions.length > 0 && (
        <Select
          className="min-w-[12rem]"
          disabled={disabled || uploading}
          label={folderLabel}
          options={folderOptions}
          value={selectedFolder}
          onChange={setSelectedFolder}
        />
      )}
      <Button
        disabled={disabled}
        onClick={() => {
          const input = document.createElement("input");
          input.type = "file";
          input.onchange = (e) => {
            handleFileChange(
              e as unknown as React.ChangeEvent<HTMLInputElement>,
            );
          };
          input.click();
        }}
      >
        <FontAwesomeIcon icon={faFileArrowUp} size="lg" />
      </Button>
      {uploading && <div>Uploading...</div>}
      {success && <div>Upload successful!</div>}
      {error && <div style={{ color: "red" }}>{error}</div>}
    </div>
  );
}
